import { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Globe, Pencil, Save } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";
import { DEFAULT_DEALER_HOST_SUFFIX } from "@/hooks/useDealerHostSuffix";

const CONFIRM_WORD = "edit";

/**
 * The domain every dealer site lives under (e.g. "skoda-auto.de") — read by
 * every authenticated user (see useDealerHostSuffix), but only a System
 * Administrator may change it, and only after typing "edit" in a warning
 * dialog: it feeds every dealer's website/legal-page/employee-photo URLs
 * (external_feeds.py, employee_photos.py, dealer_json_import.py, the
 * host-rename endpoint in server.py), so a typo here breaks all of them at once.
 */
export function DealerHostSuffixCard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isSystemAdmin = hasGlobalPermission(user, PERM.SYSTEM_DEALER_HOST_SUFFIX_MANAGE);

  const [value, setValue] = useState("");
  const [draft, setDraft] = useState("");
  const [locked, setLocked] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get(`/settings/dealer_host_suffix`);
        const saved = data?.value || "";
        setValue(saved);
        setDraft(saved);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openConfirm = () => {
    setConfirmText("");
    setConfirmOpen(true);
  };

  const unlock = () => {
    setConfirmOpen(false);
    setLocked(false);
  };

  const cancelEdit = () => {
    setDraft(value);
    setLocked(true);
  };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      toast.error(t("common.saveFailed"));
      return;
    }
    setSaving(true);
    try {
      await client.put(`/settings/dealer_host_suffix`, { value: trimmed });
      setValue(trimmed);
      setDraft(trimmed);
      setLocked(true);
      toast.success(t("common.saved"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("common.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={`mb-6 p-6 ${!isSystemAdmin ? "opacity-60" : ""}`} data-testid="settings-dealer-host-suffix-card">
      <div className="mb-1 flex items-center gap-2">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{t("settings.dealerHostSuffix")}</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        {t("settings.dealerHostSuffixSub")}
        {!isSystemAdmin && (
          <span className="ml-2 rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
            System Administrator only
          </span>
        )}
      </p>

      <div className="flex gap-2">
        <div className="max-w-xs flex-1">
          <Label className="data-label mb-1 block">{t("settings.dealerHostSuffixLabel")}</Label>
          <Input
            data-testid="settings-dealer-host-suffix-input"
            value={locked ? value : draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={DEFAULT_DEALER_HOST_SUFFIX}
            disabled={!isSystemAdmin || loading || saving || locked}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex items-end gap-2">
          {locked ? (
            <Button
              data-testid="settings-dealer-host-suffix-edit"
              variant="outline"
              onClick={openConfirm}
              disabled={!isSystemAdmin || loading}
              className="gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t("common.edit")}
            </Button>
          ) : (
            <>
              <Button
                data-testid="settings-dealer-host-suffix-cancel"
                variant="outline"
                onClick={cancelEdit}
                disabled={saving}
              >
                {t("common.cancel")}
              </Button>
              <Button
                data-testid="settings-dealer-host-suffix-save"
                variant="destructive"
                onClick={save}
                disabled={saving || !draft.trim()}
                className="gap-1.5"
              >
                <Save className="h-3.5 w-3.5" />
                {t("common.save")}
              </Button>
            </>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="settings-dealer-host-suffix-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("settings.dealerHostSuffixConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("settings.dealerHostSuffixConfirmBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            data-testid="settings-dealer-host-suffix-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={t("settings.dealerHostSuffixConfirmPlaceholder")}
            autoFocus
            autoComplete="off"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-dealer-host-suffix-confirm-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="settings-dealer-host-suffix-confirm-proceed"
              disabled={confirmText.trim().toLowerCase() !== CONFIRM_WORD}
              onClick={unlock}
            >
              {t("common.edit")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
